import React, { useState, useEffect, useContext } from "react";
import { Container, Row, Col, Form, Button, Spinner, Modal } from "react-bootstrap";
import BreadcrumbComp from "../layouts/BreadcrumbComp";
import { AuthContext } from "../../contexts/AuthContext";
import { useDispatch } from "react-redux";
import { menuActions } from "../../store/menu-slice";
import { exportToExcel } from "../utils/ExcelCreation";
import errorImage from "../../icons/error-tick-logo.svg";
import { getAllLocationForDivision } from "../../services/location-master-service";
import { getCurrentDate, getFinancialYearStart, formatDateInDDMMYYYY, RenderIf } from "../../functions/common-functions";
import { BiSelectMultiple } from "react-icons/bi";
import SingleValuePopUp from "../utils/SingleValuePopUp";
import { Multiselect } from 'multiselect-react-dropdown';
import './MultiSelectStyles.css';
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import SearchSelect from "../common/SearchSelect";
import { getDivisions } from '../../services/user-service';
import ComparisonSheetView from "./ComparisonSheetView";
import { getEnquiryReport, getFilteredEnquiryReport } from "../../services/enquiry-reports-service";
import { addLogDetails } from "../../services/logger-entry-service";

const EnquiryTenderReports = () => {
  const MySwal = withReactContent(Swal);
  const context = useContext(AuthContext);
  const dispatch = useDispatch();
  dispatch(menuActions.setMenu("Enquiry/Tender Reports"));
  const [loading, setLoading] = useState(false);
  const [showComparisonSheet, setShowComparisonSheet] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [mergeData, setMergeData] = useState([])
  const [lastFetchedFilters, setLastFetchedFilters] = useState(null);
  const [filters, setFilters] = useState({
    enquiryNumber: '',
    enquiryStatus: [],
    enquirySource: '',
    enquirySourceNumber: '',
    dateFrom: getFinancialYearStart(),
    dateTo: getCurrentDate(),
    itemCode: '',
    itemDetail: '',
    itemType: '',
    division: '',
    location: '',
    quantity: '',
    uom: '',
    gst: '',
    dueDate: '',
    price: '',
    bidSubmissionDate: '',
    salesOrderNumber: ''
  });

  const [filterData, setFilterData] = useState({
    enquiryNumbers: [],
    statuses: [],
    sources: ['Disposal', 'Contract', 'Manual'],
    divisions: [],
    locations: [],
    itemCodes: [],
    itemTypes: [],
    uoms: [],
    salesOrders: []
  });

  const [showForm, setShowForm] = useState(false);
  const [popUp, setPopUp] = useState({ name: '', type: '', key: '', value: '' });
  const [errors, setErrors] = useState({});
  const [suggestions, setSuggestions] = useState({});
  const [showSuggestions, setShowSuggestions] = useState({});

  useEffect(() => {
    fetchFilterData();
  }, []);

  const normalizeDateToYYYYMMDD = (dateStr) => {
    if (!dateStr) return null;
    const str = dateStr.toString().trim().split(' ')[0];
    if (str.includes('-')) {
      const parts = str.split('-');
      if (parts[0].length === 4) return str;
      const [dd, mm, yyyy] = parts;
      return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
    }
    if (str.includes('/')) {
      const parts = str.split('/');
      if (parts[0].length === 4) {
        const [yyyy, mm, dd] = parts;
        return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
      }
      const [dd, mm, yyyy] = parts;
      return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
    }
    const d = new Date(str);
    if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
    return null;
  };

  const fetchAndMergeData = async (filters) => {
    const normalizedFilters = {
      ...filters,
      dateFrom: normalizeDateToYYYYMMDD(filters.dateFrom) || filters.dateFrom,
      dateTo: normalizeDateToYYYYMMDD(filters.dateTo) || filters.dateTo,
      dueDate: normalizeDateToYYYYMMDD(filters.dueDate) || filters.dueDate,
      bidSubmissionDate: normalizeDateToYYYYMMDD(filters.bidSubmissionDate) || filters.bidSubmissionDate,
    };
    const data = await getFilteredEnquiryReport(normalizedFilters);
    if (!Array.isArray(data)) throw new Error("Failed to fetch report data");
    setMergeData(data);
    setLastFetchedFilters(filters);
    return data;
  };

  const fetchFilterData = async () => {
    setLoading(true);
    try {
      const [data, allDivisions] = await Promise.all([
        getFilteredEnquiryReport({ dateFrom: getFinancialYearStart(), dateTo: getCurrentDate() }),
        getDivisions()
      ]);

      if (!Array.isArray(data)) throw new Error("Failed to fetch report data");

      setMergeData(data);
      setLastFetchedFilters({ dateFrom: getFinancialYearStart(), dateTo: getCurrentDate() });

      setFilterData(prev => ({
        ...prev,
        enquiryNumbers: [...new Set(data.map(item => item.EnquiryNumber))].filter(Boolean),
        enquirySourceNumbers: [...new Set(data.map(item => item.EnquirySourceNumber))].filter(Boolean),
        itemDetails: [...new Set(data.map(item => item.ItemDetail))].filter(Boolean),
        quantities: [...new Set(data.map(item => item.Quantity))].filter(Boolean),
        statuses: ["Created", "Not Assigned", "Mail Not Sent", "Live", "Past Due Date", "Rejected", "Deleted", "Frozen"],
        divisions: allDivisions,
        itemCodes: [...new Set(data.map(item => item.ItemCode))].filter(Boolean),
        itemTypes: [...new Set(data.map(item => item.ItemType))].filter(Boolean),
        uoms: [...new Set(data.map(item => item.UOM))].filter(Boolean),
        salesOrders: [...new Set(data.map(item => item.SONumber))].filter(Boolean)
      }));
    } catch (error) {
      console.error("Error fetching filter data:", error);
      MySwal.fire({ text: "Failed to load report data. Please refresh.", imageUrl: errorImage, customClass: "swal-error" });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'enquiryNumber' && value.length > 30) return;
    if (field === 'enquirySourceNumber' && value.length > 30) return;
    if (field === 'itemDetail' && value.length > 250) return;
    if (field === 'quantity' && value.length > 50) return;
    if (field === 'gst' && value.length > 5) return;
    if (field === 'price' && value.length > 20) return;
    if (field === 'salesOrderNumber' && value.length > 20) return;

    if ((field === 'enquiryNumber' || field === 'enquirySourceNumber') && value && !/^[a-zA-Z0-9\/\-]*$/.test(value)) return;
    if (field === 'itemDetail' && value && !/^[a-zA-Z0-9 \-.,&()]*$/.test(value)) return;
    if (field === 'quantity' && value && !/^[a-zA-Z0-9 ]*$/.test(value)) return;
    if (field === 'gst' && value && !/^\d*\.?\d{0,2}$/.test(value)) return;
    if (field === 'price' && value && !/^\d*\.?\d{0,2}$/.test(value)) return;
    if (field === 'salesOrderNumber' && value && !/^[a-zA-Z0-9]*$/.test(value)) return;

    if (field === 'gst' && value && parseFloat(value) > 100) return;

    setFilters(prev => ({ ...prev, [field]: value }));

    if (field === 'division' && value) {
      fetchLocationsByDivision(value);
      setFilters(prev => ({ ...prev, location: '' }));
    }

    if (field === 'enquiryNumber' && filterData.enquiryNumbers) {
      const filtered = filterData.enquiryNumbers.filter(num => num.toLowerCase().includes(value.toLowerCase()));
      setSuggestions(prev => ({ ...prev, enquiryNumber: filtered }));
      setShowSuggestions(prev => ({ ...prev, enquiryNumber: value.length > 0 && filtered.length > 0 }));
    }

    if (field === 'enquirySourceNumber' && filterData.enquirySourceNumbers) {
      const filtered = filterData.enquirySourceNumbers.filter(num => num.toLowerCase().includes(value.toLowerCase()));
      setSuggestions(prev => ({ ...prev, enquirySourceNumber: filtered }));
      setShowSuggestions(prev => ({ ...prev, enquirySourceNumber: value.length > 0 && filtered.length > 0 }));
    }

    if (field === 'itemDetail' && filterData.itemDetails) {
      const filtered = filterData.itemDetails.filter(detail => detail.toLowerCase().includes(value.toLowerCase()));
      setSuggestions(prev => ({ ...prev, itemDetail: filtered }));
      setShowSuggestions(prev => ({ ...prev, itemDetail: value.length > 0 && filtered.length > 0 }));
    }

    if (field === 'quantity' && filterData.quantities) {
      const filtered = filterData.quantities.filter(qty => qty.toLowerCase().includes(value.toLowerCase()));
      setSuggestions(prev => ({ ...prev, quantity: filtered }));
      setShowSuggestions(prev => ({ ...prev, quantity: value.length > 0 && filtered.length > 0 }));
    }

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const fetchLocationsByDivision = async (division) => {
    try {
      const locations = await getAllLocationForDivision(division);
      setFilterData(prev => ({
        ...prev,
        locations: locations.map(loc => ({ LocationName: loc.Location, LocationCode: loc.LocationCode }))
      }));
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  const validateFilters = () => {
    const newErrors = {};
    if ((filters.dateFrom && !filters.dateTo) || (!filters.dateFrom && filters.dateTo)) {
      newErrors.dateRange = "Please enter both from and to date";
    }
    if (filters.dateFrom && filters.dateTo && new Date(filters.dateFrom) > new Date(filters.dateTo)) {
      newErrors.dateRange = "To date should be greater than or equal to From date";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const setPopUpData = (data) => {
    if (data.name === 'Item Master') {
      handleInputChange('itemCode', data.result.ItemCode);
    } else if (data.name === 'Location Master' || data.name.startsWith('Location Master:')) {
      handleInputChange('location', data.result.Location);
    }
  };

  const showPopUp = (name, type, key, value) => {
    setPopUp({ name, type, key, value });
    setShowForm(true);
  };

  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };

  const resetFilters = () => {
    setFilters({
      enquiryNumber: '',
      enquiryStatus: [],
      enquirySource: '',
      enquirySourceNumber: '',
      dateFrom: getFinancialYearStart(),
      dateTo: getCurrentDate(),
      itemCode: '',
      itemDetail: '',
      itemType: '',
      division: '',
      location: '',
      quantity: '',
      uom: '',
      gst: '',
      dueDate: '',
      salesOrderNumber: '',
      price: '',
      bidSubmissionDate: ''
    });
    setErrors({});
    setSuggestions({});
    setShowSuggestions({});
  };

  const getFilteredData = async () => {
    const filtersChanged = !lastFetchedFilters ||
      JSON.stringify(filters) !== JSON.stringify(lastFetchedFilters);

    if (!filtersChanged) return mergeData;

    setLoading(true);
    try {
      return await fetchAndMergeData(filters);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!validateFilters()) return;
    try {
      const filteredData = await getFilteredData();

      if (!filteredData || filteredData.length === 0) {
        MySwal.fire({
          text: "No Records Found.",
          imageUrl: errorImage,
          customClass: "swal-error",
        });
        setLoading(false);
        return;
      }

      // Filter for highest CurrentBid per EnquiryNumber
      const bidsByEnquiry = {};
      filteredData.forEach(item => {
        const enquiryNum = item.EnquiryNumber;
        if (!bidsByEnquiry[enquiryNum]) bidsByEnquiry[enquiryNum] = [];
        bidsByEnquiry[enquiryNum].push(item);
      });

      const exportData = [];
      Object.keys(bidsByEnquiry).forEach(enquiryNum => {
        const records = bidsByEnquiry[enquiryNum];
        const numericBids = records.map(r => parseFloat(r.CurrentBid)).filter(v => !isNaN(v));
        if (numericBids.length === 0) {
          // No numeric bids — all are "No Bid Placed" or "Regret", include all
          exportData.push(...records);
        } else {
          const maxBid = Math.max(...numericBids);
          const highestBidRecords = records.filter(r => parseFloat(r.CurrentBid) === maxBid);
          exportData.push(...highestBidRecords);
        }
      });
      console.log("The filter data for export", exportData)
      console.log("filteredData to be export", exportData.length);

      const excelData = exportData.map(item => ({
        EnquiryNumber: item.EnquiryNumber || '',
        EnquiryDate: item.InsertedOn || '',
        DueDate: item.DueDate ? formatDateInDDMMYYYY(item.DueDate) : '',
        EnquiryStatus: item.EnquiryStatus || '',
        EnquirySource: item.EnquirySource || '',
        EnquirySourceNumber: item.EnquirySourceNumber || '',
        EnquiryRaisedDateFrom: filters.dateFrom ? formatDateInDDMMYYYY(filters.dateFrom) : '',
        EnquiryRaisedDateTo: filters.dateTo ? formatDateInDDMMYYYY(filters.dateTo) : '',
        ItemCode: item.ItemCode || '',
        ItemDetail: item.ItemDetail || '',
        ItemType: item.ItemType || '',
        Division: item.Division || '',
        Location: item.Location || '',
        Quantity: item.Quantity || '',
        UOM: item.UOM || '',
        GST: item.GST || '',
        ProductClass: item.ProductClass || '',
        CommodityCode: item.CommodityCodes || '',
        LinkedBPCode: item.CustomerCode || '',
        LinkedBPName: item.LinkedBPName || '',
        FinalizedCustomerName: item.CustomerUserName || '',
        Price: item.CurrentBid || 'No Bid Placed',
        BidSubmissionDateTime: item.CurrentBidDate || '',
        RevisedDueDate: item.RevisedDueDate || 'NA',
        LNCompany: item.LogisticCompany || '',
        SalesOrderNumber: item.SONumber || '',
        Remarks: item.Remarks || ''
      }));

      const colNames = [
        "Enquiry Number", "Enquiry Date", "Due Date", "Enquiry Status", "Enquiry Source",
        "Enquiry Source Number", "Enquiry Raised Date From", "Enquiry Raised Date To",
        "Item Code", "Item Detail", "Item Type", "Division", "Location", "Quantity",
        "UOM", "GST%", "Product Class", "Commodity Code", "Linked BP Code",
        "Linked BP Name", "Finalized Customer Name", "Price₹(Excl. taxes)",
        "Bid submission date time", "Revised Due Date", "LN Company",
        "Sales Order Number", "Remarks"
      ];

      const fieldNames = [
        "EnquiryNumber", "EnquiryDate", "DueDate", "EnquiryStatus", "EnquirySource",
        "EnquirySourceNumber", "EnquiryRaisedDateFrom", "EnquiryRaisedDateTo",
        "ItemCode", "ItemDetail", "ItemType", "Division", "Location", "Quantity",
        "UOM", "GST", "ProductClass", "CommodityCode", "LinkedBPCode",
        "LinkedBPName", "FinalizedCustomerName", "Price",
        "BidSubmissionDateTime", "RevisedDueDate", "LNCompany",
        "SalesOrderNumber", "Remarks"
      ];

      exportToExcel("Enquiry_Tender_Reports", "Reports", excelData, "A1:AA1", colNames, fieldNames);

      // Add log entry for export action
      await addLogDetails(
        { Action: "Export", RecordCount: exportData.length },
        "EnquiryTenderReports",
        "Export",
        "ExportLog",
        { userName: context.userName, userId: context.userId },
        "Reports"
      );

    } catch (error) {
      console.error("Export error:", error);
      MySwal.fire({
        text: "Failed to export data. Please try again.",
        imageUrl: errorImage,
        customClass: "swal-error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewComparisonSheet = async () => {
    if (!validateFilters()) return;
    try {
      const filteredData = await getFilteredData();
      console.log("Filtered data to dispaly", filterData)

      if (!filteredData || filteredData.length === 0) {
        MySwal.fire({
          text: "No records found",
          imageUrl: errorImage,
          customClass: "swal-error",
        });
        setLoading(false);
        return;
      }

      // Get unique enquiry numbers from filtered data
      const uniqueEnquiries = [...new Set(filteredData.map(item => item.EnquiryNumber))];
      console.log("Unique enquiries", uniqueEnquiries)

      if (uniqueEnquiries.length > 1) {
        MySwal.fire({
          text: "Multiple enquiries found. Please refine filters to select a single enquiry.",
          imageUrl: errorImage,
          customClass: "swal-error",
        });
        return;
      }

      // Single unique enquiry found
      setSelectedEnquiry(filteredData[0]);
      setShowComparisonSheet(true);
    } catch (error) {
      console.error("Error:", error);
      MySwal.fire({
        text: "Error loading comparison sheet",
        imageUrl: errorImage,
        customClass: "swal-error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <div className="spinner-box"><Spinner animation="border" className="spinner" /></div>}
      <BreadcrumbComp />
      <Container fluid className="main-container">
        <div className="disposal-form">
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="g-input position-relative">
                  <Form.Control
                    id="enquiryNumber"
                    type="text"
                    name="enquiryNumber"
                    placeholder=" "
                    value={filters.enquiryNumber}
                    onChange={(e) => handleInputChange('enquiryNumber', e.target.value)}
                    onFocus={() => {
                      if (filters.enquiryNumber && filterData.enquiryNumbers) {
                        const filtered = filterData.enquiryNumbers.filter(num =>
                          num.toLowerCase().includes(filters.enquiryNumber.toLowerCase())
                        );
                        setSuggestions(prev => ({ ...prev, enquiryNumber: filtered }));
                        setShowSuggestions(prev => ({ ...prev, enquiryNumber: filtered.length > 0 }));
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowSuggestions(prev => ({ ...prev, enquiryNumber: false })), 200)}
                    maxLength={30}
                  />
                  <Form.Label htmlFor="enquiryNumber">Enquiry Number</Form.Label>
                  {showSuggestions.enquiryNumber && (
                    <div className="position-absolute bg-white border rounded shadow-sm" style={{ zIndex: 1000, width: '100%', top: '100%' }}>
                      {suggestions.enquiryNumber?.map((suggestion, index) => (
                        <div
                          key={index}
                          className="p-2 cursor-pointer"
                          style={{ cursor: 'pointer' }}
                          onMouseDown={() => {
                            handleInputChange('enquiryNumber', suggestion);
                            setShowSuggestions(prev => ({ ...prev, enquiryNumber: false }));
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#F9F2F6'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#FFFFFF'}
                        >
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <div style={{ position: 'relative' }}>
                  <Multiselect
                    options={[{ key: 'Select All' }, ...filterData.statuses.map(status => ({ key: status }))]}
                    selectedValues={filters.enquiryStatus.map(status => ({ key: status }))}
                    onSelect={(selectedList, selectedItem) => {
                      if (selectedItem.key === 'Select All') {
                        setFilters(prev => ({ ...prev, enquiryStatus: filterData.statuses }));
                      } else {
                        setFilters(prev => ({ ...prev, enquiryStatus: selectedList.filter(item => item.key !== 'Select All').map(item => item.key) }));
                      }
                    }}
                    onRemove={(selectedList, removedItem) => {
                      if (removedItem.key === 'Select All') {
                        setFilters(prev => ({ ...prev, enquiryStatus: [] }));
                      } else {
                        setFilters(prev => ({ ...prev, enquiryStatus: selectedList.filter(item => item.key !== 'Select All').map(item => item.key) }));
                      }
                    }}
                    displayValue="key"
                    placeholder="Enquiry Status"
                    showCheckbox
                    className="custom-multiselect"
                  />
                  <BiSelectMultiple
                    size={20}
                    color="#707070"
                    style={{ position: 'absolute', right: '35px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                  />
                </div>
              </Col>
            </Row>

            <Row style={{ marginTop: "8px" }}>
              <Col md={6}>
                <SearchSelect
                  name="enquirySource"
                  label="Enquiry Source"
                  data={filterData.sources}
                  value={filters.enquirySource}
                  onChange={(e) => handleInputChange('enquirySource', e.target.value)}
                  error={errors}
                  setError={setErrors}
                />
              </Col>

              <Col md={6}>
                <Form.Group className="g-input position-relative">
                  <Form.Control
                    id="enquirySourceNumber"
                    type="text"
                    name="enquirySourceNumber"
                    placeholder=" "
                    value={filters.enquirySourceNumber}
                    onChange={(e) => handleInputChange('enquirySourceNumber', e.target.value)}
                    onFocus={() => {
                      if (filters.enquirySourceNumber && filterData.enquirySourceNumbers) {
                        const filtered = filterData.enquirySourceNumbers.filter(num =>
                          num.toLowerCase().includes(filters.enquirySourceNumber.toLowerCase())
                        );
                        setSuggestions(prev => ({ ...prev, enquirySourceNumber: filtered }));
                        setShowSuggestions(prev => ({ ...prev, enquirySourceNumber: filtered.length > 0 }));
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowSuggestions(prev => ({ ...prev, enquirySourceNumber: false })), 200)}
                    maxLength={30}
                  />
                  <Form.Label htmlFor="enquirySourceNumber">Enquiry Source Number</Form.Label>
                  {showSuggestions.enquirySourceNumber && (
                    <div className="position-absolute bg-white border rounded shadow-sm" style={{ zIndex: 1000, width: '100%', top: '100%' }}>
                      {suggestions.enquirySourceNumber?.map((suggestion, index) => (
                        <div
                          key={index}
                          className="p-2 cursor-pointer"
                          style={{ cursor: 'pointer' }}
                          onMouseDown={() => {
                            handleInputChange('enquirySourceNumber', suggestion);
                            setShowSuggestions(prev => ({ ...prev, enquirySourceNumber: false }));
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#F9F2F6'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#FFFFFF'}
                        >
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="dateFrom"
                    type="date"
                    name="dateFrom"
                    placeholder=" "
                    value={filters.dateFrom}
                    onChange={(e) => handleInputChange('dateFrom', e.target.value)}
                  />
                  <Form.Label htmlFor="dateFrom">Enquiry Raised Date From</Form.Label>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="dateTo"
                    type="date"
                    name="dateTo"
                    placeholder=" "
                    value={filters.dateTo}
                    onChange={(e) => handleInputChange('dateTo', e.target.value)}
                  />
                  <Form.Label htmlFor="dateTo">Enquiry Raised Date To</Form.Label>
                  {errors.dateRange && (
                    <Form.Text className="text-danger">
                      {errors.dateRange}
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="g-input">
                  <div className="d-flex align-items-center">
                    <Form.Control
                      type="text"
                      value={filters.itemCode}
                      placeholder=" "
                      readOnly
                    />
                    <Form.Label>Item Code</Form.Label>
                    <BiSelectMultiple
                      size={20}
                      onClick={() => showPopUp('Item Master', 'Single', 'ItemCode', filters.itemCode)}
                      color="#707070"
                      style={{ marginLeft: "-2rem", cursor: "pointer" }}
                    />
                  </div>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="g-input position-relative">
                  <Form.Control
                    id="itemDetail"
                    type="text"
                    name="itemDetail"
                    placeholder=" "
                    value={filters.itemDetail}
                    onChange={(e) => handleInputChange('itemDetail', e.target.value)}
                    onFocus={() => {
                      if (filters.itemDetail && filterData.itemDetails) {
                        const filtered = filterData.itemDetails.filter(detail =>
                          detail.toLowerCase().includes(filters.itemDetail.toLowerCase())
                        );
                        setSuggestions(prev => ({ ...prev, itemDetail: filtered }));
                        setShowSuggestions(prev => ({ ...prev, itemDetail: filtered.length > 0 }));
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowSuggestions(prev => ({ ...prev, itemDetail: false })), 200)}
                    maxLength={250}
                  />
                  <Form.Label htmlFor="itemDetail">Item Detail</Form.Label>
                  {showSuggestions.itemDetail && (
                    <div className="position-absolute bg-white border rounded shadow-sm" style={{ zIndex: 1000, width: '100%', top: '100%' }}>
                      {suggestions.itemDetail?.map((suggestion, index) => (
                        <div
                          key={index}
                          className="p-2 cursor-pointer"
                          style={{ cursor: 'pointer' }}
                          onMouseDown={() => {
                            handleInputChange('itemDetail', suggestion);
                            setShowSuggestions(prev => ({ ...prev, itemDetail: false }));
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#F9F2F6'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#FFFFFF'}
                        >
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <SearchSelect
                  name="itemType"
                  label="Item Type"
                  data={filterData.itemTypes}
                  value={filters.itemType}
                  onChange={(e) => handleInputChange('itemType', e.target.value)}
                  error={errors}
                  setError={setErrors}
                />
              </Col>

              <Col md={6}>
                <SearchSelect
                  name="division"
                  label="Division "
                  data={filterData.divisions}
                  value={filters.division}
                  onChange={(e) => handleInputChange('division', e.target.value)}
                  error={errors}
                  setError={setErrors}
                />
              </Col>
            </Row>

            <Row style={{ marginTop: "20px" }}>
              <Col md={6}>
                <Form.Group className="g-input">
                  <div className="d-flex align-items-center">
                    <Form.Control
                      type="text"
                      value={filters.location}
                      placeholder=" "
                      readOnly
                    />
                    <Form.Label>Location</Form.Label>
                    <BiSelectMultiple
                      size={20}
                      onClick={() => {
                        if (filters.division) {
                          showPopUp(`Location Master:DIVISION:${filters.division}`, 'Single', 'Location', filters.location);
                        } else {
                          showPopUp('Location Master', 'Single', 'Location', filters.location);
                        }
                      }}
                      color="#707070"
                      style={{ marginLeft: "-2rem", cursor: "pointer" }}
                    />
                  </div>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="g-input position-relative">
                  <Form.Control
                    id="quantity"
                    type="text"
                    name="quantity"
                    placeholder=" "
                    value={filters.quantity}
                    onChange={(e) => handleInputChange('quantity', e.target.value)}
                    onFocus={() => {
                      if (filters.quantity && filterData.quantities) {
                        const filtered = filterData.quantities.filter(qty =>
                          qty.toLowerCase().includes(filters.quantity.toLowerCase())
                        );
                        setSuggestions(prev => ({ ...prev, quantity: filtered }));
                        setShowSuggestions(prev => ({ ...prev, quantity: filtered.length > 0 }));
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowSuggestions(prev => ({ ...prev, quantity: false })), 200)}
                    maxLength={50}
                  />
                  <Form.Label htmlFor="quantity">Quantity</Form.Label>
                  {showSuggestions.quantity && (
                    <div className="position-absolute bg-white border rounded shadow-sm" style={{ zIndex: 1000, width: '100%', top: '100%' }}>
                      {suggestions.quantity?.map((suggestion, index) => (
                        <div
                          key={index}
                          className="p-2 cursor-pointer"
                          style={{ cursor: 'pointer' }}
                          onMouseDown={() => {
                            handleInputChange('quantity', suggestion);
                            setShowSuggestions(prev => ({ ...prev, quantity: false }));
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#F9F2F6'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#FFFFFF'}
                        >
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <SearchSelect
                  name="uom"
                  label="UOM"
                  data={filterData.uoms}
                  value={filters.uom}
                  onChange={(e) => handleInputChange('uom', e.target.value)}
                  error={errors}
                  setError={setErrors}
                />
              </Col>

              <Col md={6}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="gst"
                    type="text"
                    name="gst"
                    placeholder=" "
                    value={filters.gst}
                    onChange={(e) => handleInputChange('gst', e.target.value)}
                    maxLength={5}
                  />
                  <Form.Label htmlFor="gst">GST%</Form.Label>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="dueDate"
                    type="date"
                    name="dueDate"
                    placeholder=" "
                    value={filters.dueDate}
                    onChange={(e) => handleInputChange('dueDate', e.target.value)}
                    max={getYesterdayDate()}
                  />
                  <Form.Label htmlFor="dueDate">Due Date</Form.Label>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="price"
                    type="text"
                    name="price"
                    placeholder=" "
                    value={filters.price}
                    onChange={(e) => handleInputChange('price', e.target.value)}
                    maxLength={20}
                  />
                  <Form.Label htmlFor="price">Price</Form.Label>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="bidSubmissionDate"
                    type="date"
                    name="bidSubmissionDate"
                    placeholder=" "
                    value={filters.bidSubmissionDate}
                    onChange={(e) => handleInputChange('bidSubmissionDate', e.target.value)}
                    max={getYesterdayDate()}
                  />
                  <Form.Label htmlFor="bidSubmissionDate">Bid Submission Date Time</Form.Label>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="salesOrderNumber"
                    type="text"
                    name="salesOrderNumber"
                    placeholder=" "
                    value={filters.salesOrderNumber}
                    onChange={(e) => handleInputChange('salesOrderNumber', e.target.value)}
                    maxLength={20}
                  />
                  <Form.Label htmlFor="salesOrderNumber">Sales Order Number</Form.Label>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12} className="text-center">
                <Button
                  variant="success"
                  className="btn-export-color me-2"
                  onClick={handleExport}
                  disabled={loading}
                >
                  {loading ? 'Exporting...' : 'Export to Excel'}
                </Button>
                <Button
                  variant="success"
                  className="btn-export-color me-2"
                  onClick={handleViewComparisonSheet}
                  disabled={loading}
                >
                  View Comparison Sheet
                </Button>
                <Button
                  variant="success"
                  className="btn-export-color me-2"
                  onClick={resetFilters}
                  disabled={loading}
                >
                  Reset Filters
                </Button>
              </Col>
            </Row>
          </Form>
        </div>

        <RenderIf isTrue={popUp.type === 'Single' && showForm}>
          <SingleValuePopUp
            show={showForm}
            onHide={() => setShowForm(false)}
            setSelected={setPopUpData}
            mastername={popUp.name}
            paramkey={popUp.key}
            paramvalue={popUp.value}
            backdrop="static"
          />
        </RenderIf>

        {showComparisonSheet && selectedEnquiry && (
          <ComparisonSheetView
            show={showComparisonSheet}
            onHide={() => setShowComparisonSheet(false)}
            enquiryNumber={selectedEnquiry.EnquiryNumber}
          />
        )}
      </Container>
    </>
  );
};

export default EnquiryTenderReports;
